/**
 * Tool-call trace for the assistant bubble (inline rendering replacing
 * the floating "Initializing…" progress panel).
 *
 * dral 2026-05-15: brings the chat UI in line with Claude Code /
 * Copilot — every tool the agent invokes shows up as a one-liner
 * IN the assistant bubble (collapsed by default; click `›` to expand
 * arguments + output). Pre-refactor this lived in a separate
 * floating "progressItems" panel that moved around the conversation
 * and required vertical real estate.
 *
 * The SSE backend emits progress events in two shapes:
 *
 *   1. **Structured** (preferred, present on every dispatch):
 *      ``progress_kind=tool_dispatch_started`` /
 *      ``tool_dispatch_finished`` with explicit ``tool_name``,
 *      ``skill_name``, ``arguments_summary``, ``output_summary``,
 *      ``duration_seconds`` fields at the top level of the payload.
 *
 *   2. **Legacy** (still emitted in parallel, soon-to-be-deprecated):
 *      ``event_type=progress`` with a nested ``progress: { id,
 *      stage, message, arguments_summary, output_summary }`` where
 *      ``stage`` is one of ``tool_call`` / ``tool_returned`` /
 *      ``skill_tool_call`` / ``skill_tool_returned`` / ``thinking``
 *      / ``planning`` / ``autopilot_summary``. The legacy payload's
 *      ``message`` field already includes the bash-style preview
 *      ("$ ls -la") for shell-family tools.
 *
 * The reducer accepts both shapes and folds them into a single list
 * of :class:`ToolCallLine` items per assistant turn. Pairing
 * (start ↔ finish) happens on the stable ``id``: the legacy stage
 * uses ``<id>`` for start and ``<id>:returned`` for finish; the
 * structured stage carries the tool_name + skill_name and we
 * synthesise an id from those.
 */

export type ToolCallStatus = 'running' | 'success' | 'error';

export interface ToolCallLine {
  /** Stable id for pairing start ↔ finish events. */
  id: string;
  /** User-facing verb derived from tool_name (Read / Edited / Ran …). */
  verb: string;
  /** Short target (filename / command preview / query). */
  target: string;
  /** Running / success / error. */
  status: ToolCallStatus;
  /** "+29 -6" for edits, "5 results" for search, etc. (derived). */
  delta?: string;
  /** Wall-clock duration in seconds (from the finished event). */
  durationS?: number;
  /** Full arguments string for the expand-details panel. */
  argsFull: string;
  /** Full output string for the expand-details panel (server-truncated). */
  outputFull?: string;
  /** Skill that hosted this tool call (e.g. workspace_execution). */
  skill?: string;
  /** Raw tool name for diagnostics + verb lookup. */
  toolName: string;
  /**
   * Length (char count) of the assistant's accumulated prose at the
   * moment this tool was DISPATCHED. dral 2026-05-15 (#6 interleave):
   * the renderer splits the owning assistant bubble at these offsets
   * and weaves the tool rows in between, so messages and commands
   * appear in true chronological order instead of two separate
   * tracks. Undefined for rehydrated/persisted rows (no offset
   * recorded) → such rows sort to the end (legacy "text then trace"
   * fallback).
   */
  textOffset?: number;
}

/**
 * Verb mapping table — dral 2026-05-15 first cut, awaiting review.
 *
 * Maps the canonical Protomia tool name to a one-word user-facing
 * verb shown at the start of each tool-call row. Tools not in the
 * table fall back to ``Called <tool_name>``.
 *
 * For tools that branch on ``operation`` (filesystem, text_editor),
 * the verb is chosen by the operation: filesystem.read_text → Read,
 * filesystem.write_text → Wrote, filesystem.list_dir → Listed,
 * filesystem.search_text → Searched. See ``deriveVerb`` for the
 * lookup precedence.
 */
export const VERB_MAP: Record<string, string> = {
  // Generic file + shell family
  filesystem: 'Read', // overridden by operation below
  filesystem_by_id: 'Read',
  text_editor: 'Edited',
  text_editor_by_id: 'Edited',
  shell_execution: 'Ran',
  python_execution: 'Ran',
  notebook_execute: 'Ran',
  browser_fetch: 'Fetched',
  browser_download: 'Downloaded',
  pdf_parse: 'Parsed',
  web_search: 'Searched',
  fetch_citation: 'Fetched',
  resolve_literature_refs: 'Resolved',
  workspace_tempdir: 'Allocated',
  list_workspace_temps: 'Listed',
  cleanup_workspace_temps: 'Cleaned',
  // Coding family
  repo_grep: 'Searched',
  git_status: 'Ran',
  git_diff: 'Ran',
  git_log: 'Ran',
  git_blame: 'Ran',
  propose_diff: 'Proposed',
  // Document family
  extract_docx_paragraphs: 'Extracted',
  inspect_docx_segments: 'Inspected',
  apply_docx_paragraph_edits: 'Edited',
  apply_docx_segment_edits: 'Edited',
  verify_docx_integrity: 'Verified',
  add_docx_comments: 'Commented',
  delete_docx_comments: 'Deleted',
  edit_docx_comment_authors: 'Edited',
  insert_docx_field_codes: 'Inserted',
  insert_docx_runs: 'Inserted',
  italicize_reference_journals: 'Formatted',
  // Endnote / citation family
  list_endnote_refs: 'Listed',
  add_endnote_citation: 'Cited',
  add_bibliography_entries: 'Added',
  convert_docx_to_latex: 'Converted',
  compile_latex: 'Compiled',
  // Chemistry family
  molecule_calc: 'Computed',
  submit_prepared_job: 'Submitted',
  check_jobs: 'Checked',
  cancel_job: 'Cancelled',
  cancel_session_jobs: 'Cancelled',
  compute_esp_cubes: 'Computed',
  plot_mol_esp: 'Plotted',
  convert_image_to_xyz: 'Converted',
  read_result_key_by_id: 'Read',
  reaction_compose: 'Composed',
  probe_environment: 'Probed',
  // Periodic DFT
  periodic_calc: 'Computed',
  build_slab: 'Built',
  build_supercell: 'Built',
  place_adsorbate: 'Placed',
  generate_defect_surface: 'Built',
  compute_adsorption_energy: 'Computed',
  compute_electron_density_difference: 'Computed',
  // Scheduler
  scheduler_query: 'Queried',
  // Skill-as-tool delegations (orchestrator → skill agent). These
  // arrive as ``progress.stage=tool_call/tool_returned`` with
  // progress.id == the bare skill name. The args summary is a
  // natural-language request ("Read the full content of …"), so the
  // verb stays generic and the request becomes the row target. dral
  // 2026-05-15 — verbs here are first-cut; rename freely (see
  // VERB_MAPPING.md). Distinct verbs help the user scan which
  // sub-agent did what without expanding.
  workspace_execution: 'Explored',
  coding_assistant: 'Coded',
  general_assistant: 'Assisted',
  chemistry_calc: 'Computed',
  reaction_workflow: 'Reacted',
  literature_search: 'Researched',
  periodic_dft: 'Computed',
  document_editor: 'Edited',
  manuscript_revision: 'Revised',
  reviewer_response: 'Drafted',
  surface_builder: 'Built',
  mlp_dataset_catalog: 'Queried',
};

/** Skill names that arrive as orchestrator-level "tools". When the
 *  tool name is one of these we tag the row's ``skill`` field too so
 *  the expand panel shows the delegation context. */
export const SKILL_NAMES: ReadonlySet<string> = new Set([
  'workspace_execution', 'coding_assistant', 'general_assistant',
  'chemistry_calc', 'reaction_workflow', 'literature_search',
  'periodic_dft', 'document_editor', 'manuscript_revision',
  'reviewer_response', 'surface_builder', 'mlp_dataset_catalog',
]);

/**
 * Derive the verb for a tool call. Special-cases the ``filesystem``
 * family which branches on the ``operation`` argument: read_text →
 * Read, write_text → Wrote, list_dir → Listed, search_text → Searched.
 */
export function deriveVerb(toolName: string, argsFull: string): string {
  if (toolName === 'filesystem' || toolName === 'filesystem_by_id') {
    if (/\boperation"\s*:\s*"write_text"/.test(argsFull)) return 'Wrote';
    if (/\boperation"\s*:\s*"list_dir"/.test(argsFull)) return 'Listed';
    if (/\boperation"\s*:\s*"search_text"/.test(argsFull)) return 'Searched';
    return 'Read';
  }
  return VERB_MAP[toolName] || `Called ${toolName}`;
}

/**
 * Derive the user-facing target for a tool call. Tries hard to keep
 * it compact — a single line at most.
 *
 *   filesystem.read_text {path}            → "<basename>"
 *   shell_execution {command}              → '"<first 60 chars>"'
 *   web_search {query}                     → '"<query>"'
 *   browser_fetch {url}                    → '<url host + path>'
 *   git_status / git_diff / git_log        → 'git <verb>' (already verbed)
 *   *default*                              → first 80 chars of argsFull
 */
export function deriveTarget(toolName: string, argsFull: string): string {
  // JSON-shaped arguments_summary
  if (toolName.startsWith('filesystem') || toolName.startsWith('text_editor')) {
    const pathMatch = argsFull.match(/"path"\s*:\s*"([^"]+)"/);
    if (pathMatch) {
      const fullPath = pathMatch[1];
      return fullPath.split('/').pop() || fullPath;
    }
  }
  if (toolName === 'web_search') {
    const queryMatch = argsFull.match(/"query"\s*:\s*"([^"]+)"/);
    if (queryMatch) return `"${queryMatch[1]}"`;
  }
  if (toolName === 'browser_fetch' || toolName === 'browser_download') {
    const urlMatch = argsFull.match(/"url"\s*:\s*"([^"]+)"/);
    if (urlMatch) {
      try {
        const u = new URL(urlMatch[1]);
        return u.host + u.pathname;
      } catch {
        return urlMatch[1].slice(0, 60);
      }
    }
  }
  // Shell-style preview: arguments_summary already starts with "$ "
  if (argsFull.startsWith('$ ')) {
    const cmd = argsFull.slice(2).trim();
    return cmd.length > 80 ? `"${cmd.slice(0, 77)}…"` : `"${cmd}"`;
  }
  // Repo grep / chemistry — show the first quoted value or first ~80 chars.
  const firstQuoted = argsFull.match(/"([^"]{2,})"/);
  if (firstQuoted) {
    const v = firstQuoted[1];
    return v.length > 60 ? `${v.slice(0, 57)}…` : v;
  }
  const trimmed = argsFull.trim();
  return trimmed.length > 80 ? `${trimmed.slice(0, 77)}…` : trimmed;
}

/**
 * Try to extract a compact delta (e.g. "+29 -6") from the output
 * summary of an edit / write tool. Returns undefined when nothing
 * useful is detected.
 */
export function deriveDelta(toolName: string, outputFull: string | undefined): string | undefined {
  if (!outputFull) return undefined;
  if (toolName === 'text_editor' || toolName === 'text_editor_by_id') {
    const m = outputFull.match(/Applied (\d+) text edit/);
    if (m) return `${m[1]} edit${m[1] === '1' ? '' : 's'}`;
  }
  if (toolName === 'web_search') {
    const m = outputFull.match(/returned (\d+) result/);
    if (m) return `${m[1]} result${m[1] === '1' ? '' : 's'}`;
  }
  if (toolName === 'repo_grep') {
    const m = outputFull.match(/(\d+) match/);
    if (m) return `${m[1]} match${m[1] === '1' ? '' : 'es'}`;
  }
  if (toolName === 'filesystem' && /bytes_written/.test(outputFull)) {
    const m = outputFull.match(/"bytes_written"\s*:\s*(\d+)/);
    if (m) {
      const bytes = parseInt(m[1], 10);
      if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${bytes} B`;
    }
  }
  return undefined;
}

/**
 * Drop the trailing ``:returned`` suffix the legacy stage uses to
 * pair finish events with their starts.
 */
function normaliseId(id: string): string {
  return id.replace(/:returned$/, '');
}

/**
 * Fold a single SSE payload into the current tool-call list.
 *
 * Returns a NEW list (immutable update) — the caller wires this
 * into ``setToolTrace(prev => foldToolCallEvent(prev, data))``.
 *
 * Recognised inputs:
 *
 *   * Structured ``progress_kind=tool_dispatch_started``
 *   * Structured ``progress_kind=tool_dispatch_finished``
 *   * Legacy ``progress.stage`` ∈ { tool_call, skill_tool_call }
 *   * Legacy ``progress.stage`` ∈ { tool_returned, skill_tool_returned }
 *
 * Everything else returns ``prev`` unchanged.
 */
/**
 * Stable, collision-resistant key for a tool call's full arguments.
 * dral 2026-05-15: the structured-event id previously used
 * ``argsFull.slice(0, 32)`` — two different commands sharing a long
 * common prefix (``cd /mlatom/home/dral/projects/aitomia-jctc && …A``
 * vs ``…B``) produced the SAME id, so the fold reducer
 * (``prev.filter(l => l.id !== id)``) silently REPLACED the earlier
 * row with the later one ("same type only shows the latest, quick
 * flicker before the previous disappears"). Hashing the FULL args
 * makes distinct invocations distinct while a single call's
 * started/finished events (which both carry the same
 * ``arguments_summary``) still hash identically → correct pairing.
 */
function _argsKey(argsFull: string): string {
  let h = 5381;
  for (let i = 0; i < argsFull.length; i += 1) {
    h = ((h << 5) + h + argsFull.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36) + ':' + argsFull.length;
}

export function foldToolCallEvent(
  prev: ToolCallLine[],
  data: any,
  /** Assistant prose length at this event's arrival — stamped onto a
   *  tool line's ``textOffset`` when the call STARTS so the renderer
   *  can interleave it at the right point in the message stream. */
  assistantTextLen?: number,
): ToolCallLine[] {
  const offset = typeof assistantTextLen === 'number' ? assistantTextLen : undefined;
  // Preserve the dispatch-time offset across the start→finish update.
  const offsetFor = (id: string, fallback?: number): number | undefined => {
    const existing = prev.find((l) => l.id === id);
    return existing?.textOffset ?? fallback;
  };
  // --- Structured tool-dispatch events --------------------------------------
  if (data?.event_type === 'progress' && typeof data?.progress_kind === 'string') {
    const kind = data.progress_kind;
    if (kind === 'tool_dispatch_started') {
      const toolName: string = data.tool_name || 'unknown';
      const skill: string | undefined = data.skill_name || undefined;
      const argsFull: string = data.arguments_summary || '';
      const id = `${skill || 'orchestrator'}:${toolName}:${data.thread_id || ''}:${_argsKey(argsFull)}`;
      const verb = deriveVerb(toolName, argsFull);
      const target = deriveTarget(toolName, argsFull);
      // Replace existing same-id entry if any (defensive — duplicate
      // start events from the dual stream).
      const filtered = prev.filter((line) => line.id !== id);
      return [...filtered, {
        id, verb, target,
        status: 'running',
        argsFull,
        skill,
        toolName,
        textOffset: offsetFor(id, offset),
      }];
    }
    if (kind === 'tool_dispatch_finished') {
      const toolName: string = data.tool_name || 'unknown';
      const skill: string | undefined = data.skill_name || undefined;
      const argsFull: string = data.arguments_summary || '';
      const outputFull: string | undefined = data.output_summary || undefined;
      const durationS: number | undefined = data.duration_seconds;
      const status: ToolCallStatus = data.status === 'error' ? 'error' : 'success';
      const id = `${skill || 'orchestrator'}:${toolName}:${data.thread_id || ''}:${_argsKey(argsFull)}`;
      const delta = deriveDelta(toolName, outputFull);
      const verb = deriveVerb(toolName, argsFull);
      const target = deriveTarget(toolName, argsFull);
      // Find existing same-id row to preserve order; if missing,
      // synthesize a new finished row at the end (defensive).
      const idx = prev.findIndex((line) => line.id === id);
      const updated: ToolCallLine = {
        id, verb, target, status,
        durationS,
        argsFull,
        outputFull,
        delta,
        skill,
        toolName,
        textOffset: offsetFor(id, offset),
      };
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = updated;
        return next;
      }
      return [...prev, updated];
    }
    return prev;
  }
  // --- Legacy progress.stage events -----------------------------------------
  const progress = data?.progress;
  if (!progress || typeof progress !== 'object') return prev;
  const stage: string = progress.stage || '';
  if (stage !== 'tool_call' && stage !== 'skill_tool_call'
      && stage !== 'tool_returned' && stage !== 'skill_tool_returned') {
    return prev;
  }
  const rawId: string = progress.id || '';
  if (!rawId) return prev;
  // dral 2026-05-15 fix: strip the ``:returned`` pairing suffix
  // BEFORE parsing skill/tool. The old code ran the skill:tool
  // regex against the raw id, so ``workspace_execution:returned``
  // parsed as skill='workspace_execution' tool='returned' — a
  // bogus tool name that produced "Called returned …" rows AND
  // (because the row never matched a known verb) made the
  // orchestrator skill-as-tool returns fall through to the old
  // boxed progress panel instead of the inline trace.
  const id = normaliseId(rawId);
  const argsFull: string = progress.arguments_summary || progress.message || '';
  const outputFull: string | undefined = progress.output_summary || undefined;

  // Parse skill / tool from the SUFFIX-STRIPPED id. Three shapes:
  //   1. "<skill>: <tool>"  (skill-nested: stage=skill_tool_*)
  //   2. "<skill>_<tool>"   (skill-nested start; only when <tool>
  //                          is a known verb-mapped tool)
  //   3. "<name>"           (orchestrator skill-as-tool — the
  //                          skill IS the tool from the
  //                          orchestrator's POV, e.g.
  //                          "workspace_execution")
  let toolName = '';
  let skill: string | undefined = undefined;
  const m1 = id.match(/^([a-z_]+):\s*([a-z_]+)$/);
  if (m1) {
    skill = m1[1];
    toolName = m1[2];
  } else {
    // dral 2026-05-15: composite "<skill>_<tool>" where BOTH the
    // skill and the tool contain underscores
    // (e.g. ``workspace_execution_filesystem`` →
    // skill=workspace_execution tool=filesystem,
    // ``workspace_execution_shell_execution`` →
    // skill=workspace_execution tool=shell_execution). The old
    // lazy ``([a-z_]+?)_([a-z_]+?)`` split mis-parsed these (gave
    // skill="workspace" tool="execution_filesystem"), so they
    // fell through to the "Called workspace_execution_filesystem"
    // raw-id row with the raw JSON args shown as the target.
    // Match against the known skill prefixes first.
    let matched = false;
    for (const sk of SKILL_NAMES) {
      if (id.startsWith(sk + '_')) {
        const rest = id.slice(sk.length + 1);
        if (
          VERB_MAP[rest] ||
          rest === 'filesystem' ||
          rest === 'filesystem_by_id' ||
          rest === 'text_editor' ||
          rest === 'text_editor_by_id'
        ) {
          skill = sk;
          toolName = rest;
          matched = true;
          break;
        }
      }
    }
    if (!matched) {
      const m2 = id.match(/^([a-z_]+?)_([a-z_]+?)$/);
      if (m2 && VERB_MAP[m2[2]] && !SKILL_NAMES.has(id)) {
        skill = m2[1];
        toolName = m2[2];
      } else {
        // Bare name. If it's a known skill, tag skill= so the
        // expand panel shows the delegation context.
        toolName = id;
        if (SKILL_NAMES.has(id)) {
          skill = id;
        }
      }
    }
  }
  const verb = deriveVerb(toolName, argsFull);
  const target = deriveTarget(toolName, argsFull);

  // dral 2026-05-15 (#1/#6): the legacy progress.id for skill-tools
  // is the bare ``skill: tool`` (NOT per-invocation), so 15 distinct
  // shell_execution calls under workspace_execution all shared one
  // id → each new start ``filter(id!==...)`` REPLACED the prior →
  // the live trace showed "2 actions" when 15 ran (only the block
  // view, keyed on msg.id, showed the truth). Make the row id
  // per-invocation by appending the full-args hash. Pairing: the
  // legacy *_returned finish carries different text ("[… returned
  // in Xs]") so its args-hash won't match the start's — so on
  // finish, if no exact-id row, fall back to the MOST-RECENT still-
  // running row with the same ``skill:tool`` prefix (legacy tools
  // are dispatched/returned sequentially, so this pairs correctly).
  const prefix = `${skill || 'orchestrator'}:${toolName}:`;
  const invId = `${prefix}${_argsKey(argsFull)}`;

  if (stage === 'tool_call' || stage === 'skill_tool_call') {
    const filtered = prev.filter((line) => line.id !== invId);
    return [...filtered, {
      id: invId, verb, target,
      status: 'running',
      argsFull,
      skill,
      toolName,
      textOffset: offsetFor(invId, offset),
    }];
  }
  // tool_returned / skill_tool_returned
  let idx = prev.findIndex((line) => line.id === invId);
  if (idx < 0) {
    // No exact match (legacy finish text differs from start) →
    // pair with the latest still-running row of the same skill:tool.
    for (let i = prev.length - 1; i >= 0; i -= 1) {
      if (prev[i].status === 'running' && prev[i].id.startsWith(prefix)) {
        idx = i;
        break;
      }
    }
  }
  const pairedId = idx >= 0 ? prev[idx].id : invId;
  const status: ToolCallStatus = /failed|error/i.test(outputFull || '') ? 'error' : 'success';
  const delta = deriveDelta(toolName, outputFull);
  // Try to grab duration from "returned in X.Xs" in progress.message.
  let durationS: number | undefined = undefined;
  const durMatch = (progress.message || '').match(/returned in (\d+(?:\.\d+)?)s/);
  if (durMatch) durationS = parseFloat(durMatch[1]);
  const updated: ToolCallLine = {
    id: pairedId, verb, target, status,
    durationS, argsFull, outputFull, delta, skill, toolName,
    // preserve the paired row's dispatch offset (keeps interleave
    // position stable across the start→finish update).
    textOffset: prev[idx]?.textOffset ?? offsetFor(pairedId, offset),
  };
  if (idx >= 0) {
    const next = [...prev];
    next[idx] = updated;
    return next;
  }
  return [...prev, updated];
}

/**
 * Post-turn one-liner — collapses a completed trace into a single
 * "Read 2 files, edited 1, ran 3 commands" summary à la Claude Code.
 * Distinct from Claude Code's exact wording (verbs are protomia's
 * own, see VERB_MAP). Returns undefined when the trace is empty.
 */
export function summariseToolTrace(trace: ToolCallLine[]): string | undefined {
  if (!trace.length) return undefined;
  // dral 2026-05-15: count EVERY row (incl. any still marked
  // ``running`` — a stalled/unpaired call still happened and is
  // still visible). Pre-fix running rows were skipped, so the
  // collapsed header under-reported vs. the rows the user could see
  // ("Ran 2 commands, but I see more").
  const verbCounts = new Map<string, number>();
  for (const line of trace) {
    const key = line.verb.toLowerCase();
    verbCounts.set(key, (verbCounts.get(key) || 0) + 1);
  }
  if (!verbCounts.size) return undefined;
  // Phrase as "Read 2 files, ran 3 commands, edited 1 file".
  const phrased: string[] = [];
  for (const [verb, n] of verbCounts) {
    // Pick a generic noun per verb family. Keep it short.
    const noun =
      (verb === 'read' || verb === 'wrote' || verb === 'edited') ? 'file' :
      (verb === 'ran') ? 'command' :
      (verb === 'searched' || verb === 'fetched' || verb === 'queried') ? 'query' :
      (verb === 'submitted' || verb === 'computed' || verb === 'built') ? 'job' :
      'action';
    phrased.push(`${verb} ${n} ${noun}${n === 1 ? '' : 's'}`);
  }
  // Capitalise the first verb only.
  const first = phrased[0];
  phrased[0] = first.charAt(0).toUpperCase() + first.slice(1);
  return phrased.join(', ');
}
